import {
  amountText,
  type BabyPeriods,
  BOTTLE_UNIT_NAME,
  BOTTLE_UNIT_TITLE,
  type BottleUnit,
  formatAmount,
  fromOz,
  PERIODS_SHOWN,
} from "@babia/checks";
import { lazy, type ReactNode, Suspense, useId, useMemo, useState } from "react";
import { track } from "../../lib/analytics.ts";
import {
  bottleDepth,
  bottleKey,
  bottleRows,
  changeRows,
  dailyAverage,
  type DayTotals,
  niceMax,
  type SleepRow,
} from "../../lib/overview.ts";
import { type TrendKind, type Zoom, ZOOM_UNIT } from "../../lib/periods.ts";
import type { Baby } from "../../lib/queries.ts";
import { pluralize } from "../../lib/time.ts";
import { CHANGES, DIRTY, WET } from "./chart-series.ts";
import { ChartTabs } from "./chart-tabs.tsx";
import type { DaySeries } from "./day-bars.tsx";
import { AddBirthday, PeriodTrends } from "./period-trends.tsx";
import { PerBaby, Section } from "./section.tsx";
import { SleepChart } from "./sleep-chart.tsx";
import { ZoomChips } from "./zoom-chips.tsx";

// ECharts is most of a chart's weight, and only this page draws one: load it
// with the first chart, not with the log.
const DayBars = lazy(() => import("./day-bars.tsx").then((m) => ({ default: m.DayBars })));

/** An average in stored ounces as the chart shows it, in the household's unit. */
const shown = (oz: number | null, unit: BottleUnit) => (oz === null ? null : fromOz(oz, unit));

/** A color key's swatch: one color, or the wet and dirty stripes. */
function Swatch({ colors }: { colors: string[] }) {
  const [a, b] = colors;
  return (
    <span
      className="mr-1 inline-block size-2.5 rounded-[2px] align-[-1px]"
      style={{
        background: b ? `repeating-linear-gradient(45deg, ${a} 0 2px, ${b} 2px 4px)` : a,
      }}
      aria-hidden="true"
    />
  );
}

const TABS: { key: TrendKind; label: string }[] = [
  { key: "bottles", label: "Bottles" },
  { key: "changes", label: "Changes" },
  { key: "sleep", label: "Sleep" },
];

/** The key under the tabs: what a bar is, in the household's unit. */
function noteFor(kind: TrendKind, zoom: Zoom, unit: BottleUnit): ReactNode {
  const kinds = (
    <>
      <Swatch colors={[WET]} />
      wet only <Swatch colors={[DIRTY]} />
      dirty only <Swatch colors={[WET, DIRTY]} />
      both
    </>
  );
  if (zoom === "days")
    return {
      bottles: `${BOTTLE_UNIT_TITLE[unit]} a day · a block per bottle · today is hatched`,
      changes: <>Changes a day · {kinds} · today is hatched</>,
      sleep: "Noon to noon, newest night first · solid is the longest stretch",
    }[kind];
  const p = ZOOM_UNIT[zoom];
  const hatched = `this ${p} is hatched from its first full day`;
  return {
    bottles: `Average ${BOTTLE_UNIT_NAME[unit]} a day over each ${p}'s full days, by ${p}s old · ${hatched}`,
    changes: (
      <>
        Average changes a day over each {p}'s full days · {kinds} · {hatched}
      </>
    ),
    sleep: `Average sleep a night, noon to noon, over each ${p}'s whole nights · solid is the longest stretch · this ${p} is hatched from its first whole night`,
  }[kind];
}

/** The section's note: what the charts cover. */
function spanFor(zoom: Zoom, limited: boolean): string {
  if (zoom === "weeks") return `By week of life · up to ${PERIODS_SHOWN.week} weeks`;
  if (zoom === "months") return "By month of life · up to a year";
  return limited ? "The last two days" : "The last two weeks";
}

/** "1st", "2nd", "3rd", "4th", … "11th", "21st". */
function ordinal(n: number): string {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? "th" : (["th", "st", "nd", "rd"][n % 10] ?? "th");
  return `${n}${suffix}`;
}

/** The weeks and months of life, read once a parent picks Weeks or Months. */
export interface LongTrends {
  /** Every baby's periods (absent for a baby with no birthday); null until loaded. */
  periods: ReadonlyMap<string, BabyPeriods> | null;
  loading: boolean;
  /** Why the periods never loaded. */
  error: string | null;
  /** When the periods shown were loaded, if a refresh since then failed. */
  staleSince: number | null;
}

/**
 * The trends, one chart at a time. Days shows the last two weeks: bottles (a
 * block per bottle, so a column shows the day's ounces and its bottle count),
 * changes (stacked by kind, each in its color), and the sleep window. Weeks
 * and Months show each week or month of life as its average a day. A tab
 * shows only for a type the family tracks and has logged. One zoom and one
 * tab drive every baby's column, so twins compare the same chart on the same
 * scale.
 */
export function Trends({
  babies,
  all: household,
  per,
  sleepRows,
  tracking,
  unit,
  loading,
  limited,
  zoom,
  onZoom,
  long,
}: {
  /** The babies shown: one on a phone with tabs. */
  babies: Baby[];
  /** Every baby in the household, for the birthday line. */
  all: Baby[];
  /** Each baby's day totals, oldest first. */
  per: ReadonlyMap<string, DayTotals[]>;
  sleepRows: (babyId: string) => SleepRow[];
  tracking: { sleep: boolean; diaper: boolean };
  /** The unit bottles show in. */
  unit: BottleUnit;
  /** The two weeks are loading. */
  loading: boolean;
  /** The two weeks never loaded: only the live two days are here. */
  limited: boolean;
  zoom: Zoom;
  onZoom: (zoom: Zoom) => void;
  long: LongTrends;
}) {
  const span = limited ? "the last two days" : "the last two weeks";
  const panelId = useId();
  const [chosen, setChosen] = useState<TrendKind | null>(null);

  const all = useMemo(() => [...per.values()], [per]);
  const days = (id: string) => per.get(id) ?? [];
  const flat = all.flat();
  const amountMax = niceMax(Math.max(0, ...flat.map((d) => fromOz(d.oz, unit))));
  const changeMax = niceMax(Math.max(0, ...flat.map((d) => d.changes)));

  const depth = useMemo(() => bottleDepth(all), [all]);
  const bottleSeries = useMemo<DaySeries[]>(
    () =>
      Array.from({ length: depth }, (_, n) => ({
        key: bottleKey(n),
        label: `${ordinal(n + 1)} bottle`,
        colors: ["var(--accent)"],
      })),
    [depth],
  );
  const rows = useMemo(
    () => new Map([...per].map(([id, d]) => [id, bottleRows(d, depth, unit)])),
    [per, depth, unit],
  );
  const changes = useMemo(() => new Map([...per].map(([id, d]) => [id, changeRows(d)])), [per]);

  // A kind logged in the two weeks keeps its tab in Weeks and Months too, so
  // the tabs don't jump in once the longer read arrives.
  const periods = zoom === "days" ? null : long.periods;
  const longHas = (kind: TrendKind) =>
    [...(periods?.values() ?? [])].some((p) => p[kind].length > 0);
  const has: Record<TrendKind, boolean> = {
    bottles: flat.some((d) => d.bottles > 0) || longHas("bottles"),
    changes: tracking.diaper && (flat.some((d) => d.changes > 0) || longHas("changes")),
    sleep: tracking.sleep && (flat.some((d) => d.sleepMs > 0) || longHas("sleep")),
  };
  const available = TABS.filter((t) => has[t.key]);
  const tab = available.find((t) => t.key === chosen) ?? available[0];

  const select = (key: TrendKind) => {
    setChosen(key);
    track("chart tab selected", { tab: key, zoom });
  };
  const zoomTo = (next: Zoom) => {
    if (next === zoom) return;
    onZoom(next);
    track("chart zoom selected", { zoom: next });
  };
  const placeholder = <div className="h-[194px]" aria-hidden="true" />;

  const body = (kind: TrendKind) =>
    zoom === "days" ? (
      <div aria-busy={loading} className={loading ? "opacity-50" : undefined}>
        <PerBaby babies={babies}>
          {(b) =>
            kind === "sleep" ? (
              <SleepChart rows={sleepRows(b.id)} />
            ) : (
              <Suspense fallback={placeholder}>
                {kind === "bottles" ? (
                  <DayBars
                    chart="bottles"
                    days={days(b.id)}
                    rows={rows.get(b.id) ?? []}
                    series={bottleSeries}
                    max={amountMax}
                    average={shown(
                      dailyAverage(days(b.id), (d) => d.oz),
                      unit,
                    )}
                    unit={unit}
                    describe={(d) =>
                      `${formatAmount(d.oz, unit)} in ${pluralize(d.bottles, "bottle", "bottles")}`
                    }
                    valueText={(amount) => amountText(amount, unit)}
                  />
                ) : (
                  <DayBars
                    chart="changes"
                    days={days(b.id)}
                    rows={changes.get(b.id) ?? []}
                    series={CHANGES}
                    max={changeMax}
                    average={dailyAverage(days(b.id), (d) => d.changes)}
                    unit=""
                    describe={(d) =>
                      `${pluralize(d.changes, "change", "changes")} · ${d.wet} wet, ${d.dirty} dirty`
                    }
                    valueText={(n) => String(n)}
                  />
                )}
              </Suspense>
            )
          }
        </PerBaby>
      </div>
    ) : (
      <PeriodTrends
        babies={babies}
        periods={periods ?? new Map()}
        kind={kind}
        zoom={zoom}
        unit={unit}
        loading={long.loading}
        error={long.error}
        staleSince={long.staleSince}
      />
    );

  const quiet = "mt-3 py-3 text-[14px] italic opacity-60";
  const nothing =
    zoom === "days"
      ? loading
        ? "Loading the last two weeks…"
        : `Nothing logged in ${span} yet.`
      : long.loading
        ? `Loading the ${zoom} since birth…`
        : "Nothing logged since birth yet.";

  return (
    <Section title="Trends" note={spanFor(zoom, limited)}>
      <ZoomChips zoom={zoom} onSelect={zoomTo} />
      {zoom !== "days" && !household.some((b) => b.birthDate !== null) ? (
        <AddBirthday many={household.length > 1} zoom={zoom} />
      ) : tab ? (
        <>
          <ChartTabs
            tabs={available}
            selected={tab.key}
            onSelect={select}
            panelId={panelId}
            label="Chart"
          />
          <div
            role={available.length > 1 ? "tabpanel" : undefined}
            id={panelId}
            aria-labelledby={available.length > 1 ? `${panelId}-${tab.key}` : undefined}
          >
            {/* Under the tabs, so a longer note never moves them. */}
            <p className="mt-3 text-[12px] text-ink/60">{noteFor(tab.key, zoom, unit)}</p>
            {body(tab.key)}
          </div>
        </>
      ) : zoom !== "days" && long.error ? (
        // With nothing in the two weeks, no tab shows: say why the longer read is missing.
        <p className="mt-3 text-[14px] text-accent-2">
          Couldn't load the {zoom} since birth — {long.error}.
        </p>
      ) : (
        <p className={quiet}>{nothing}</p>
      )}
    </Section>
  );
}
